import {
  Box,
  FormControl,
  FormLabel,
  Image,
  Input,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import {
  CustomDrawerBackButton,
  CustomDrawerFooter,
  CustomDrawerHeader,
  CustomFormErrorMessage,
  PrimaryButton,
} from '../../../../components/custom';
import CustomDropdown from '../../../../components/custom/CustomDropdown/CustomDropdown';
import TransactionReceipt from '../../../../components/custom/TransactionReceipt';
import { handleNumericInput } from '../../../../constants/validationConstants';
import {
  currencyUnformat,
  sortBundles,
} from '../../../../helpers/utilityFunctions';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getBillTransactionsAsync } from '../../../../store/slices/billsPayment/billTransactionsSlice';
import { IGetBillers } from '../../../../store/slices/billsPayment/getBillersCategorySlice';
import {
  payBillAsync,
  resetPayBill,
} from '../../../../store/slices/billsPayment/payBillSlice';
import CreateTransactionPin from '../../../TransactionPin/CreateTransactionPin';
import { TransactionPinForm } from '../TransactionPinForm';
import InternetPurchaseSummary from './InternetPurchaseSummary';

interface IBuyPlanFormProps {
  providers: IGetBillers[];
  selectedBillCategory: string;
  initialBillerName?: string;
  initialCustomerId?: string;
}

export const BuyPlanForm = ({
  providers,
  selectedBillCategory,
  initialBillerName,
  initialCustomerId,
}: IBuyPlanFormProps) => {
  const dispatch = useAppDispatch();

  const [page] = useState(1);
  const [limit] = useState(10);

  const [isFixedPrice, setIsFixedPrice] = useState(false);

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  const [step, setStep] = useState(0);

  const [amountLimitError] = useState('');

  const [insufficientBalanceError, setInsufficientBalanceError] = useState('');

  const { value } = useAppSelector((state) => state.auth);
  const { value: accountNumber } = useAppSelector(
    (state) => state.accountBalance
  );

  const { value: acctBalValue } = useAppSelector(
    (state) => state.accountBalance
  );

  const { value: outletsValue } = useAppSelector((state) => state.getOutlets);

  const availableBalance = acctBalValue?.data?.availableBalance;
  const availableBalanceInNaira = availableBalance ? availableBalance / 100 : 0;

  const getMappedNetworkForDropdown = (networkName: string) => {
    const networkOption = providerOptions.find(
      (option) => option.name === networkName
    );

    return {
      name: networkName,
      icon: networkOption ? <Image src={networkOption.icon} alt="" /> : null,
    };
  };

  useEffect(() => {
    dispatch(resetPayBill());
  }, [dispatch]);

  const providerOptions = useMemo(() => {
    return providers
      .filter((provider) => !provider.name?.toLowerCase().includes('data'))
      .map((provider) => ({
        id: provider.id || '',
        name: provider.name || '',
        icon: provider.imageUrl || '',
      }));
  }, [providers]);

  const initialValues = {
    provider: initialBillerName ?? '',
    packageName: '',
    customerId: initialCustomerId || '',
    amount: '',
  };

  const validationSchema = Yup.object().shape({
    provider: Yup.string().required('Provider is required'),
    packageName: Yup.string().required('Package is required'),
    customerId: Yup.string().required('Customer ID is required'),
    amount: Yup.string().required('Amount is required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Do nothing
    },
  });

  const {
    errors,
    touched,
    values,
    isValid,
    getFieldProps,
    setFieldValue,
    handleSubmit,
  } = formik;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || (/^\+?\d*$/.test(value) && value.length <= 14)) {
      setFieldValue('customerId', value);
    }
  };

  const customerName = `${value?.data?.user?.firstName} ${value?.data?.user?.lastName}`;
  const customerAccountNo = accountNumber?.data?.accountNumber;
  const accountNameFromStorage = localStorage.getItem('accountName');

  const outletId =
    outletsValue?.data?.find(
      (outlet) =>
        outlet?.account?.accountNumber === customerAccountNo &&
        outlet?.account?.accountName === accountNameFromStorage
    )?.id ?? '';

  const handleFinalSubmit = async (pinValues: { pin: string }) => {
    const selectedPackage = packages.find((b) => b.name === values.packageName);
    const response = await dispatch(
      payBillAsync({
        outletId,
        amount: Number(values.amount),
        customerId: values.customerId || '',
        customerAccountNo: customerAccountNo || 0,
        customerName: customerName,
        billItemId: selectedPackage?.id || '',
        pin: pinValues.pin || '',
        billCategory: 'data',
        billProvider: values.provider,
      })
    );

    if (response?.meta?.requestStatus === 'fulfilled') {
      dispatch(
        getBillTransactionsAsync({
          accountNo: customerAccountNo,
          category: selectedBillCategory.toUpperCase(),
          page,
          limit,
        })
      );
    }
  };
  const { status: paymentStatus, value: paymentValue } = useAppSelector(
    (state) => state.billsPayment
  );

  const packages = useMemo(
    () =>
      providers?.find((n) => n.name === values.provider)
        ?.currentProviderBillerItems || [],
    [providers, values.provider]
  );

  const bundleNames = packages.map((bundle) => bundle.name);

  const sortedPackageNames = sortBundles(bundleNames);
  const billerId = packages?.find((n) => n.name === values.packageName)?.id;

  const billDetails = {
    customerName: customerName,
    customerId: values.customerId ?? '',
    billCategory: 'data',
    billItemId: packages.find((b) => b.name === values.packageName)?.id ?? '',
    billerName: values.provider,
    billerId: billerId ?? '',
  };

  useEffect(() => {
    if (values.packageName) {
      setIsFixedPrice(false);
      const selectedPackage = packages.find(
        (b) => b.name === values.packageName
      );
      if (selectedPackage?.isFixedPrice) {
        setFieldValue('amount', selectedPackage?.amount?.toString());
        setIsFixedPrice(true);
      }
      setFieldValue('packageId', selectedPackage?.id || '');
    }
  }, [packages, setFieldValue, values.packageName]);

  const handleGoForward = () => {
    if (packages.length > 0 && isValid) {
      const selectedPackage = packages.find(
        (b) => b.name === values.packageName
      );
      const packageAmount = Number(selectedPackage?.amount || 0);

      if (packageAmount <= availableBalanceInNaira) {
        setStep((prevStep) => prevStep + 1);
      } else {
        setInsufficientBalanceError(
          'Insufficient balance to purchase this package.'
        );
      }
    }
  };

  const handleGoBack = () => {
    setStep((currentStep) => currentStep - 1);
  };

  const renderStep = () => {
    const steps = [
      <Box display="flex" flexDirection="column" py="18px">
        <CustomDrawerHeader startsFromBeginning>Internet</CustomDrawerHeader>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={8} flex="1">
              <Box mb={6}>
                <Text fontWeight="500" color="#000" mb={1}>
                  Recipient Information
                </Text>
                <Text color="gray.300" fontSize=".875rem">
                  Streamline transactions with seamless information management
                </Text>
              </Box>

              <FormControl
                id="provider"
                isInvalid={Boolean(touched.provider && errors.provider)}
              >
                <FormLabel>Provider</FormLabel>
                <CustomDropdown
                  options={providerOptions || []}
                  placeholder="Select an option"
                  getMappedNetwork={getMappedNetworkForDropdown}
                  setFieldValue={setFieldValue}
                  value={values.provider}
                  fieldValue="provider"
                />
                <CustomFormErrorMessage>
                  {touched.provider && errors.provider}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="packageName"
                isInvalid={Boolean(touched.packageName && errors.packageName)}
              >
                <FormLabel>Package</FormLabel>
                <Select
                  disabled={!sortedPackageNames.length}
                  placeholder="Select an option"
                  isInvalid={Boolean(touched.packageName && errors.packageName)}
                  {...getFieldProps('packageName')}
                  onChange={(e) => {
                    const selectedPackageName = e.target.value;
                    const selectedPackage = packages.find(
                      (b) => b.name === selectedPackageName
                    );
                    setFieldValue('packageName', selectedPackageName);
                    setFieldValue('packageId', selectedPackage?.id || '');
                  }}
                  textTransform="capitalize"
                >
                  {sortedPackageNames?.map((selectedPackageName, index) => (
                    <option key={index} value={selectedPackageName}>
                      {selectedPackageName}
                    </option>
                  ))}
                </Select>
                <CustomFormErrorMessage>
                  {touched.packageName && errors.packageName}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl id="customerId">
                <FormLabel>Customer ID</FormLabel>
                <Input
                  {...getFieldProps('customerId')}
                  as="textarea"
                  placeholder="Enter your ID"
                  sx={{
                    pt: 4,
                  }}
                  onChange={handlePhoneNumberChange}
                  onKeyDown={(e) => handleNumericInput(e, 11)} //change this when exact figure is known
                  maxLength={11}
                />
              </FormControl>

              <FormControl
                id="amount"
                isInvalid={Boolean(
                  touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)
                )}
              >
                <FormLabel>Amount</FormLabel>
                <Input
                  disabled={isFixedPrice || !values.packageName}
                  {...getFieldProps('amount')}
                  value={
                    isFixedPrice
                      ? Number(values.amount).toLocaleString()
                      : values.amount
                  }
                  as="textarea"
                  placeholder="Enter amount"
                  isInvalid={Boolean(
                    touched.amount &&
                      (insufficientBalanceError ||
                        amountLimitError ||
                        errors.amount)
                  )}
                  sx={{
                    pt: 4,
                  }}
                />
                <CustomFormErrorMessage>
                  {touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)}
                </CustomFormErrorMessage>
              </FormControl>
            </Stack>

            <CustomDrawerFooter>
              <PrimaryButton
                onClick={handleGoForward}
                type="submit"
                w="full"
                isDisabled={Boolean(insufficientBalanceError)}
              >
                Continue
              </PrimaryButton>
            </CustomDrawerFooter>
          </Form>
        </FormikProvider>
      </Box>,
      <>
        <CustomDrawerBackButton onClick={handleGoBack} />

        <InternetPurchaseSummary
          summary={{
            providerName: values.provider,
            packageName: values.packageName,
            accountName: '',
            customerId: values.customerId,
            amount: Number(values.amount).toLocaleString(),
            date: new Date(),
          }}
          handleSubmit={handleGoForward}
        />
      </>,
      <>
        <TransactionPinForm
          isSuccess={paymentStatus === 'success'}
          isLoading={paymentStatus === 'loading'}
          isError={paymentStatus === 'failed'}
          onSubmit={handleFinalSubmit}
          goToReceipt={handleGoForward}
          step={step}
          setStep={(currentStep) => setStep(currentStep)}
          statusBoxTitle={paymentValue?.message}
          statusBoxText=""
          billDetails={billDetails}
          twoFactor={{
            type: 'bill_payment',
            amountKobo: Math.round(currencyUnformat(values.amount) * 100),
          }}
        />
      </>,
      <TransactionReceipt
        date={new Date().toDateString()}
        amount={currencyUnformat(values.amount)}
        transactionType="Bill Payment"
        transactionReference={'----'}
        senderName={accountNameFromStorage ?? customerName}
        narration="test"
        provider={values.provider}
        device={values.customerId}
        transactionFee="₦0.00"
        senderAccountNumber={customerAccountNo}
        showAmountInWords={false}
      />,
    ];

    return <>{steps[step]}</>;
  };

  return (
    <div>
      {!userHasPin ? (
        <CreateTransactionPin setUserHasPin={setUserHasPin} />
      ) : (
        renderStep()
      )}
    </div>
  );
};
